import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FaHeart, FaUserCheck, FaSignInAlt,
  FaArrowLeft, FaCrown, FaSearch,
  FaFilter, FaStar, FaMapMarkerAlt,
  FaGraduationCap, FaBriefcase
} from "react-icons/fa";
import { Spin, message, Pagination, Tag, Empty } from "antd";
import { CrownOutlined, TeamOutlined } from "@ant-design/icons";
import { UserDataGetwt } from "../../Api/CoreApi";
import Footer from "../Footer/Footer";
import { BASE_API } from "../../Api/Api";
import "./AllProfiles.css";

const AllProfiles = () => {
  const navigate = useNavigate();
  const baseurl = BASE_API;

  const id = localStorage.getItem("user_id");
  const isLoggedIn = !!id;

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [myProfile, setMyProfile] = useState(null);
  const [filterStats, setFilterStats] = useState({
    sameCaste: 0,
    sameReligion: 0,
    total: 0,
    oppositeGender: "",
  });

  // ============================================
  // Login Guard
  // ============================================
  useEffect(() => {
    if (!isLoggedIn) {
      message.warning("Please login to view profiles");
      navigate("/User_Login", {
        replace: true,
        state: { from: "/all-profiles" },
      });
      return;
    }
    fetchAllProfiles();
  }, []);

  // ============================================
  // Smart Filter
  // ============================================
   const applySmartFilter = (rawProfiles, effectiveId) => {
    if (!rawProfiles || rawProfiles.length === 0) {
      setProfiles([]);
      setFilterStats({ sameCaste: 0, sameReligion: 0, total: 0, oppositeGender: "" });
      return;
    }

    if (!effectiveId) {
      setProfiles([]);
      return;
    }

    // 1. Apna profile dhundo
    const me = rawProfiles.find(
      (p) =>
        String(p.bid) === String(effectiveId) ||
        String(p.user_id) === String(effectiveId) ||
        String(p.User_id) === String(effectiveId)
    );

    setMyProfile(me || null);

    if (!me) {
      setProfiles([]);
      setFilterStats({ sameCaste: 0, sameReligion: 0, total: 0, oppositeGender: "" });
      return;
    }

    // 2. Apna profile filter se hatao
    let filtered = rawProfiles.filter(
      (p) =>
        String(p.bid) !== String(effectiveId) &&
        String(p.user_id) !== String(effectiveId) &&
        String(p.User_id) !== String(effectiveId)
    );

    // 3. Opposite gender filter lagao (Male -> Female, Female -> Male)
    const oppositeGender = me.gender === "Male" ? "Female" : "Male";
    if (me.gender) {
      filtered = filtered.filter(
        (p) => p.gender && p.gender.toLowerCase() === oppositeGender.toLowerCase()
      );
    }

    const myCaste = (me.caste || "").toLowerCase().trim();
    const myReligion = (me.religion || "").toLowerCase().trim();

    let matchedProfiles = [];

    // 4. 🔴 STRICT FILTER: Agar user ka caste nahi hai, toh koi match nahi dikhega.
    // Agar caste hai, toh sirf exact same caste wale hi filter honge.
    if (myCaste) {
      matchedProfiles = filtered.filter((p) => {
        const pCaste = (p.caste || "").toLowerCase().trim();
        return pCaste === myCaste; // Sirf exact caste matching profiles accept honge
      });
    } else {
      console.warn("⚠️ Login user has no caste configured.");
      matchedProfiles = []; // Khali array agar apna caste set nahi hai
    }

    // 5. Baaki filters (jaise same religion) ko priority sorting ke liye score do
    const scored = matchedProfiles.map((profile) => {
      let score = 0;
      const pReligion = (profile.religion || "").toLowerCase().trim();

      // Caste toh already 100% same hi hai sabka, ab religion match karne wale ko top par sort karenge
      if (myReligion && pReligion === myReligion) score += 50;

      return { ...profile, _matchScore: score };
    });

    // Score ke mutabik high-to-low sort karein (taki same caste + same religion top par aaye)
    scored.sort((a, b) => b._matchScore - a._matchScore);

    // Stats calculations update karein
    const sameCasteCount = scored.length; // Kyuki ab sabhi same caste wale hi hain
    const sameReligionCount = scored.filter(
      (u) => myReligion && (u.religion || "").toLowerCase().trim() === myReligion
    ).length;

    setFilterStats({
      sameCaste: sameCasteCount,
      sameReligion: sameReligionCount,
      total: scored.length,
      oppositeGender,
    });

    setProfiles(scored);
    setCurrentPage(1);
  };

  // ============================================
  // Fetch Profiles
  // ============================================
  const fetchAllProfiles = async () => {
    setLoading(true);
    try {
      const response = await UserDataGetwt();
      let rawProfiles = [];
      let myUserId = id;

      if (Array.isArray(response)) {
        rawProfiles = response;
      } else if (response?.profiles) {
        rawProfiles = response.profiles;
        if (response.my_user_id) myUserId = response.my_user_id;
      }

      applySmartFilter(rawProfiles, myUserId);
    } catch (error) {
      console.error("Error:", error);
      message.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Helpers
  // ============================================
  const getDisplayName = (user) =>
    user.firstname || user.first_name || user.username || "User";

  const getProfileImage = (user) => {
    if (user.pic) {
      if (user.pic.startsWith("http")) return user.pic;
      return `${baseurl}${user.pic}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      getDisplayName(user)
    )}&background=667eea&color=fff&size=200&bold=true`;
  };

  const formatProfileId = (user) => {
    if (!user) return "N/A";
    if (user.profile_id) return user.profile_id;
    if (user.ref) return `${String(user.ref).padStart(6, "0")}`;
    return "N/A";
  };

  const getUserId = (item) =>
    item.bid || item.User_id || item.user_id || item.id;

  const getMatchType = (profile) => {
    if (!myProfile) return null;
    const myCaste = (myProfile.caste || "").toLowerCase().trim();
    const myReligion = (myProfile.religion || "").toLowerCase().trim();
    const pCaste = (profile.caste || "").toLowerCase().trim();
    const pReligion = (profile.religion || "").toLowerCase().trim();
    const casteMatch = myCaste && pCaste === myCaste;
    const religionMatch = myReligion && pReligion === myReligion;
    if (casteMatch && religionMatch) return "both";
    if (casteMatch) return "caste";
    if (religionMatch) return "religion";
    return null;
  };

  const getMatchPercent = (profile) => {
    if (!myProfile) return 0;
    let score = 40; // Base score
    const myCaste = (myProfile.caste || "").toLowerCase().trim();
    const myReligion = (myProfile.religion || "").toLowerCase().trim();
    const pCaste = (profile.caste || "").toLowerCase().trim();
    const pReligion = (profile.religion || "").toLowerCase().trim();

    if (myCaste && pCaste === myCaste) score += 25; // Hamesha true hoga
    if (myReligion && pReligion === myReligion) score += 20; // Extra points agar religion bhi same ho
    if (myProfile.state && profile.state && myProfile.state === profile.state) score += 10;
    if (myProfile.district && profile.district && myProfile.district === profile.district) score += 5;
    return Math.min(score, 100);
  };

  // Pagination
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProfiles = profiles.slice(startIndex, startIndex + pageSize);

  if (!isLoggedIn) {
    return (
      <div className="ap-redirect-screen">
        <Spin size="large" />
        <p>Redirecting to login...</p>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="ap-page">
      {/* ✅ Hero Header */}
      <div className="ap-hero">
        <div className="ap-hero-bg-shapes">
          <div className="ap-shape ap-shape-1"></div>
          <div className="ap-shape ap-shape-2"></div>
          <div className="ap-shape ap-shape-3"></div>
        </div>

        <div className="ap-hero-content">
          {/* Navigation */}
          <div className="ap-nav">
            <button className="ap-back-btn" onClick={() => navigate("/")}>
              <FaArrowLeft />
              <span>Home</span>
            </button>
            <div className="ap-nav-title">
              <FaHeart className="ap-nav-heart" />
              <span>Find Your Match</span>
            </div>
            <button
              className="ap-refresh-btn"
              onClick={fetchAllProfiles}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Hero Title */}
          <div className="ap-hero-text">
            <h1>
              Discover Your
              <span className="ap-gradient-text"> Perfect Match</span>
            </h1>
            <p>
              {myProfile
                ? `${profiles.length} ${filterStats.oppositeGender} profiles matching your preferences`
                : "Loading personalized matches for you..."}
            </p>
          </div>

          {/* ✅ User Welcome Card */}
          {myProfile && (
            <div className="ap-welcome-card">
              <div className="ap-welcome-left">
                <div
                  className="ap-welcome-avatar"
                  style={{
                    background:
                      myProfile.gender === "Male"
                        ? "linear-gradient(135deg, #667eea, #764ba2)"
                        : "linear-gradient(135deg, #f093fb, #f5576c)",
                  }}
                >
                  {myProfile.gender === "Male" ? "👨" : "👩"}
                </div>
                <div className="ap-welcome-info">
                  <h3>Welcome, {getDisplayName(myProfile)}!</h3>
                  
                </div>
              </div>

              <div className="ap-welcome-stats">
                <div className="ap-welcome-stat-item">
                  <span className="ap-welcome-stat-num">
                    {filterStats.total}
                  </span>
                  <span className="ap-welcome-stat-label">Total</span>
                </div>
                {filterStats.sameCaste > 0 && (
                  <div className="ap-welcome-stat-item ap-stat-caste">
                    <span className="ap-welcome-stat-num">
                      {filterStats.sameCaste}
                    </span>
                    <span className="ap-welcome-stat-label">Caste</span>
                  </div>
                )}
                {/* {filterStats.sameReligion > 0 && (
                  <div className="ap-welcome-stat-item ap-stat-religion">
                    <span className="ap-welcome-stat-num">
                      {filterStats.sameReligion}
                    </span>
                    <span className="ap-welcome-stat-label">Religion</span>
                  </div>
                )} */}
              </div>
            </div>
          )}

          {/* ✅ Filter Tags */}
          {myProfile && (
            <div className="ap-filter-tags">
              {filterStats.sameCaste > 0 && (
                <div className="ap-filter-tag ap-tag-gold">
                  <FaCrown />
                  <span>
                    {filterStats.sameCaste} Same Caste ({myProfile.caste})
                  </span>
                </div>
              )}
              {/* {filterStats.sameReligion > 0 && (
                <div className="ap-filter-tag ap-tag-purple">
                  <span>🙏</span>
                  <span>
                    {filterStats.sameReligion} Same Religion (
                    {myProfile.religion})
                  </span>
                </div>
              )} */}
              <div className="ap-filter-tag ap-tag-default">
                <TeamOutlined />
                <span>{filterStats.total} Matches Found</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Content */}
      <div className="ap-main">
        {/* Page Info Bar */}
        <div className="ap-info-bar">
          <div className="ap-info-left">
            <FaFilter className="ap-info-icon" />
            <span>
              Showing {startIndex + 1}-
              {Math.min(startIndex + pageSize, profiles.length)} of{" "}
              {profiles.length} profiles
            </span>
          </div>
          <div className="ap-info-right">
            <span>
              Page {currentPage} of{" "}
              {Math.ceil(profiles.length / pageSize) || 1}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="ap-loading-container">
            <div className="ap-loading-animation">
              <div className="ap-pulse-heart">💖</div>
              <div className="ap-loading-rings">
                <div className="ap-ring ap-ring-1"></div>
                <div className="ap-ring ap-ring-2"></div>
                <div className="ap-ring ap-ring-3"></div>
              </div>
            </div>
            <h3>Finding Your Perfect Matches...</h3>
            <p>Analyzing compatibility based on your preferences</p>
          </div>
        ) : profiles.length > 0 ? (
          <>
            {/* ✅ Profile Cards */}
            <div className="ap-cards-grid">
              {paginatedProfiles.map((item, index) => {
                const userId = getUserId(item);
                const matchType = getMatchType(item);
                const matchPercent = getMatchPercent(item);

                return (
                  <Link
                    to={`/user-profile/${userId}`}
                    key={`profile-${userId}-${index}`}
                    className="ap-card-link"
                  >
                    <div className={`ap-profile-card ${matchType ? `ap-match-${matchType}` : ""}`}>
                      {/* Match Badge */}
                      {matchType && (
                        <div className={`ap-match-badge ap-badge-${matchType}`}>
                          {matchType === "both" && (
                            <>
                              <FaCrown /> Best Match
                            </>
                          )}
                          {matchType === "caste" && (
                            <>
                              <FaStar /> Caste Match
                            </>
                          )}
                          {matchType === "religion" && (
                            <>
                              🙏 Religion Match
                            </>
                          )}
                        </div>
                      )}

                      {/* Match Percentage */}
                      <div className={`ap-match-percent ${matchType === "both" ? "ap-percent-best" : matchType === "caste" ? "ap-percent-caste" : matchType === "religion" ? "ap-percent-religion" : ""}`}>
                        {matchPercent}%
                      </div>

                      {/* Image Section */}
                      <div className="ap-card-image-section">
                        <img
                          src={getProfileImage(item)}
                          alt={getDisplayName(item)}
                          className="ap-card-photo"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              getDisplayName(item)
                            )}&background=667eea&color=fff&size=200&bold=true`;
                          }}
                        />
                        <div className="ap-card-overlay"></div>

                        {/* Online Indicator */}
                        {item.is_online && (
                          <div className="ap-online-dot"></div>
                        )}

                        {/* Verified Badge */}
                        {item.verified && (
                          <div className="ap-verified-badge">
                            <FaUserCheck /> Verified
                          </div>
                        )}

                        {/* Profile ID */}
                        <div className="ap-profile-id">
                          {formatProfileId(item)}
                        </div>

                        {/* Bottom Gradient Info */}
                        <div className="ap-card-bottom-info">
                          <h3 className="ap-card-username">
                            {getDisplayName(item)}
                            {item.verified && <span className="ap-verified-tick">✓</span>}
                          </h3>
                          <p className="ap-card-age-location">
                            {item.age && `${item.age} yrs`}
                            {item.age && item.district && " • "}
                            {item.district && item.district}
                          </p>
                        </div>
                      </div>

                      {/* Details Section */}
                      <div className="ap-card-details">
                        {/* Info Pills */}
                        <div className="ap-detail-pills">
                          {item.caste && (
                            <span
                              className={`ap-pill ${
                                matchType === "both" || matchType === "caste"
                                  ? "ap-pill-matched"
                                  : ""
                              }`}
                            >
                              🙏 {item.caste}
                              {(matchType === "both" ||
                                matchType === "caste") && (
                                <span className="ap-pill-check">✓</span>
                              )}
                            </span>
                          )}
                          {item.religion && (
                            <span
                              className={`ap-pill ${
                                matchType === "both" ||
                                matchType === "religion"
                                  ? "ap-pill-religion-matched"
                                  : ""
                              }`}
                            >
                              🕉️ {item.religion}
                              {(matchType === "both" ||
                                matchType === "religion") && (
                                <span className="ap-pill-check">✓</span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Extra Info */}
                        <div className="ap-extra-info">
                          {item.course && (
                            <div className="ap-extra-item">
                              <FaGraduationCap />
                              <span>{item.course}</span>
                            </div>
                          )}
                          {item.job_title && (
                            <div className="ap-extra-item">
                              <FaBriefcase />
                              <span>{item.job_title}</span>
                            </div>
                          )}
                          {item.state && (
                            <div className="ap-extra-item">
                              <FaMapMarkerAlt />
                              <span>
                                {item.district
                                  ? `${item.district}, ${item.state}`
                                  : item.state}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="ap-card-cta">
                          <span className="ap-view-profile-btn">
                            View Profile
                            <span className="ap-arrow">→</span>
                          </span>
                          <div className="ap-card-heart-btn">
                            <FaHeart />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* ✅ Pagination */}
            <div className="ap-pagination-wrapper">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={profiles.length}
                onChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} profiles`
                }
              />
            </div>
          </>
        ) : (
          /* ✅ Empty State */
          <div className="ap-empty-state">
            <div className="ap-empty-illustration">
              <div className="ap-empty-circle">
                <span>💕</span>
              </div>
              <div className="ap-empty-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h2>No Matching Profiles Found</h2>
            <p>
              {myProfile
                ? `We couldn't find ${filterStats.oppositeGender} profiles matching your caste "${myProfile.caste || "Not Set"}" and religion "${myProfile.religion || "Not Set"}".`
                : "Please complete your profile to see personalized matches."}
            </p>
            <p className="ap-empty-hint">
              💡 Update your profile with correct caste and religion for better
              matches
            </p>
            <div className="ap-empty-actions">
              <button className="ap-btn-primary" onClick={() => navigate("/")}>
                <FaArrowLeft /> Back to Home
              </button>
              <button
                className="ap-btn-secondary"
                onClick={() => navigate("/profile")}
              >
                ✏️ Update Profile
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AllProfiles;